/* eslint-disable no-nested-ternary */
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { AllRsaKeys } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";

import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";

import ContextMenu, {
  ContextOption,
} from "../components/context-menu/ContextMenu";
import styles from "./migrate-key.module.scss";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import { getRsaKeys } from "../utils/helpers/data/rsa-keys";
import Select, { Option } from "../components/select/Select";

const RsaKeys: FunctionComponent = () => {
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [reset, setReset] = useState(false);
  const [dataSource, setDataSource] = useState<AllRsaKeys[]>([]);

  const [issuerCode, setIssuerCode] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const [rsaKeysOnView, setRsaKeysOnView] = useState<AllRsaKeys | null>(null);

  const { notify } = useContext(LayoutContext);

  const statusOptions: Option[] = [
    {
      label: "ACTIVE",
      value: "ACTIVE",
    },
    {
      label: "EXPIRED",
      value: "EXPIRED",
    },
    {
      label: "EXPIRING",
      value: "EXPIRING",
    },
  ];

  const fetchRsaKeys = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);

    const { error, message, data } = await getRsaKeys(
      pageNumber,
      pagination.pageSize,
      issuerCode,
      status
    );

    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch all RSA Keys: ${message}`);
      return;
    }

    setDataSource(data.records);
    setPagination({ ...pagination, total: data.total, pageNumber });
  };

  const handleViewReport = async () => {
    setLoading(true);

    try {
      setPagination({ ...pagination, pageNumber: 1 });

      await fetchRsaKeys(1);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching all RSA Keys.", error);
    }
  };

  const resetFilters = () => {
    setReset(true);

    setIssuerCode("");
    setStatus("");
    setPagination({ ...pagination, pageNumber: 1 });
  };

  useEffect(() => {
    if (reset) {
      fetchRsaKeys();
      setReset(false);
    }
  }, [reset, issuerCode, status]);

  const showResetFilter = issuerCode || status;

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  const columns: Column<AllRsaKeys>[] = [
    {
      title: "Issuer Code",
      dataIndex: "issuerCode",
      key: "issuerCode",
    },
    {
      title: "Activated",
      dataIndex: "activated",
      key: "activated",
      render: (activated: boolean) => (activated ? "True" : "False"),
    },

    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },
    {
      title: "Created Date",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },
    {
      title: "Migrated Date",
      dataIndex: "migratedDate",
      key: "migratedDate",
      render: (date: string | null) =>
        date ? formatDate(date) : <span className="text-gray-400">Null</span>,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={`tag smaller ${
            status === "ACTIVE"
              ? "success"
              : status === "EXPIRING"
              ? "expiring"
              : "error"
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "",
      render: (_: unknown, migrate: AllRsaKeys) => {
        const options: ContextOption[] = [
          {
            label: "View details",
            onClick: () => setRsaKeysOnView(migrate),
          },
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      },
    },
  ];

  useEffect(() => {
    fetchRsaKeys();
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">All RSA Keys</h1>

      <div className={styles.cont}>
        <div className={styles.containerFlex}>
          <div className={styles.container}>
            <div className="input-group ">
              <Input
                value={issuerCode || ""}
                onChange={(value) => setIssuerCode(value as string)}
                placeholder="Enter Issuer Code"
                responsive
              />
            </div>

            <div className="input-group ">
              <Select
                options={statusOptions}
                value={status || ""}
                onSelect={(value) => setStatus(value as string)}
                placeholder="Select Status"
                responsive
              />
            </div>
          </div>
        </div>

        <div className={styles.inputView}>
          <Button
            type="accent"
            size="large"
            className="strong no-pdf-print"
            onClick={handleViewReport}
          >
            Filter
          </Button>
          <span className={styles.button} />
        </div>
      </div>

      {showResetFilter && (
        <Button type="accent" size="small" onClick={resetFilters}>
          RESET
        </Button>
      )}
      <br />

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={(pageNumber) =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={(pageSize) =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />

      <RsaKeyViewModal
        rsaKey={rsaKeysOnView}
        cancel={() => setRsaKeysOnView(null)}
        visible={Boolean(rsaKeysOnView)}
      />
    </main>
  );
};

interface desKeysModalProps {
  rsaKey: AllRsaKeys | null;
  cancel: () => void;
  visible: boolean;
}

const RsaKeyViewModal: FunctionComponent<desKeysModalProps> = ({
  rsaKey: _rsaKey,
  cancel,
  visible,
}) => {
  const [loading] = useState(false);
  const [rsaKey, setDesKey] = useState<AllRsaKeys | null>(_rsaKey);

  useEffect(() => {
    setDesKey(_rsaKey);
  }, [_rsaKey]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{rsaKey?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {rsaKey?.createdOn ? formatDate(rsaKey.createdOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated Date:</span>
            <span className={styles.value}>
              {rsaKey?.lastUpdatedOn ? formatDate(rsaKey.lastUpdatedOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Code:</span>
            <span className={styles.value}>{rsaKey?.issuerCode || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Expiry Date:</span>
            <span className={styles.value}>
              {" "}
              {rsaKey?.expiryDate ? formatDate(rsaKey.expiryDate) : "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Activated:</span>
            <span className={styles.value}>
              {rsaKey?.activated != null
                ? rsaKey.activated
                  ? "True"
                  : "False"
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Label:</span>
            <span className={styles.value}>{rsaKey?.keyLabel || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Env Flag:</span>
            <span className={styles.value}>{rsaKey?.envFlag || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Activation Date:</span>
            <span className={styles.value}>
              {" "}
              {rsaKey?.activationDate
                ? formatDate(rsaKey.activationDate)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Description:</span>
            <span className={styles.value}>{rsaKey?.description || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Index:</span>
            <span className={styles.value}>{rsaKey?.keyIndex || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Version:</span>
            <span className={styles.value}>{rsaKey?.keyVersion || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Migrated Date:</span>
            <span className={styles.value}>
              {" "}
              {rsaKey?.migratedDate ? formatDate(rsaKey.migratedDate) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Status:</span>
            <span className={styles.value}>{rsaKey?.status || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Modulus Length:</span>
            <span className={styles.value}>
              {rsaKey?.keyModulusLength || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Key Exponent Value:</span>
            <span className={styles.value}>
              {rsaKey?.keyExponentValue || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Authentication Data:</span>
            <span className={styles.value}>
              {rsaKey?.authenticationData || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Hash Identifier:</span>
            <span className={styles.value}>
              {rsaKey?.hashIdentifier || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Signature Identifier:</span>
            <span className={styles.value}>
              {rsaKey?.signatureIdentifier || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Bin:</span>
            <span className={styles.value}>{rsaKey?.issuerBin || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Scheme Data:</span>
            <span className={styles.value}>{rsaKey?.schemeData || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Has Key:</span>
            <span className={styles.value}>{rsaKey?.hasKey || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Has Old Key:</span>
            <span className={styles.value}>{rsaKey?.hasOldKey || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Public Key:</span>
            <span className={styles.value}>
              {rsaKey?.issuerPublicKey || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Private Key:</span>
            <span className={styles.value}>
              {rsaKey?.issuerPrivateKey || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Certificate Length:</span>
            <span className={styles.value}>
              {rsaKey?.certificateLength || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Self Signed Certificate:</span>
            <span className={styles.value}>
              {rsaKey?.selfSignedCertificate || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Hash:</span>
            <span className={styles.value}>{rsaKey?.hash || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Mac:</span>
            <span className={styles.value}>{rsaKey?.mac || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Old Private Key:</span>
            <span className={styles.value}>
              {rsaKey?.oldPrivateKey || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Name:</span>
            <span className={styles.value}>
              {rsaKey?.auditableName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Auditable Id:</span>
            <span className={styles.value}>{rsaKey?.auditableId || "N/A"}</span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default RsaKeys;
